import { useEffect, useRef } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  box-sizing: content-box;
`;

const Tab = styled.div`
  color: #404040;
  font-size: 16px;
  font-weight: 500;
  margin-right: 20px;
  cursor: pointer;
  transition: color 300ms ease;

  :first-letter {
    text-transform: capitalize;
  }

  &.active {
    color: ${({ theme }) => theme.colors.primary['blue-1']};
  }
`;

const Highlight = styled.div`
  position: absolute;
  top: 100%;
  height: 2px;
  transform: translateY(-100%);
  background-color: ${({ theme }) => theme.colors.primary['blue-1']};
  transition: transform 300ms ease-out, width 300ms ease;
`;

type Tab = { name: React.ReactNode; value: any };
type Props = {
  className?: string;
  tabs: Array<Tab>;
  onTabSelect(value: Tab['value']): void;
  selectedTab: string;
};

const Tabs = (props: Props) => {
  const ref = useRef(null);
  const { className, tabs, onTabSelect, selectedTab } = props;

  useEffect(() => {
    const active = ref.current.querySelector('.active');
    const highlight = ref.current.querySelector('.highlight');

    requestAnimationFrame(() => {
      if (ref.current) {
        const { x: containerX } = ref.current.getBoundingClientRect();
        const { width, x } = active.getBoundingClientRect();
        highlight.style.transform = `translate(${x - containerX}px, -50%)`;
        highlight.style.width = width + 'px';
      }
    });
  }, [selectedTab]);

  return (
    <Container className={className} ref={ref}>
      {tabs.map(({ name, value }) => (
        <Tab
          key={value}
          className={selectedTab === value ? 'active' : ''}
          onClick={() => onTabSelect(value)}
          tabIndex={0}
        >
          {name}
        </Tab>
      ))}
      <Highlight className="highlight" />
    </Container>
  );
};

export default Tabs;
